import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { ApiFactory } from '../api/easypos-api';
import { ICashSession, IShift } from '../api/model/requests';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { styles as sharedStyles } from '../styles/shared-styles';
import { designPrimitives } from '../styles/design-primitives';
import { resolveRouterPath, router } from '../router';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

@customElement('cash-sessions-page')
export class CashSessionsPage extends Base {
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _sessions: ICashSession[] = [];
  // J2: the cashier/device/open-close times live on the session's parent Shift.
  // Join shifts (by cashSessionId) so each session row can render its context.
  @state() private _shiftBySessionId: Record<string, IShift> = {};
  @state() private _range: '7d' | '30d' | '90d' = '7d';

  static styles = [
    sharedStyles,
    designPrimitives,
    css`
      :host {
        display: block;
        max-width: 900px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      .range-strip {
        display: inline-flex;
        gap: 4px;
        margin-bottom: 16px;
        padding: 4px;
        background: var(--s-canvas-strong, rgba(15, 30, 55, 0.06));
        border-radius: 999px;
      }
      .range-strip button {
        appearance: none;
        border: none;
        background: transparent;
        color: var(--s-ink-soft, #666);
        font: inherit;
        font-weight: 600;
        font-size: 0.85rem;
        padding: 7px 16px;
        border-radius: 999px;
        cursor: pointer;
        transition:
          background 140ms ease,
          color 140ms ease;
      }
      .range-strip button.active {
        background: var(--s-surface, #fff);
        color: var(--s-ink, currentColor);
        box-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 30, 55, 0.08));
      }
      .session-card {
        margin-bottom: 10px;
        padding: 14px 16px;
        display: block;
        width: 100%;
        box-sizing: border-box;
        text-align: left;
        font: inherit;
        background: var(--s-surface, #fff);
        border: 1px solid var(--s-line, rgba(15, 30, 55, 0.08));
        border-radius: 16px;
        box-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 30, 55, 0.05));
        cursor: pointer;
        transition:
          transform 140ms ease,
          box-shadow 140ms ease;
      }
      .session-card:hover {
        transform: translateY(-1px);
        box-shadow: var(--app-shadow-md, 0 6px 18px rgba(15, 30, 55, 0.08));
      }
      .session-card:focus-visible {
        outline: none;
        box-shadow: var(--sl-focus-ring, 0 0 0 3px rgba(15, 30, 55, 0.25));
      }
      .session-row {
        display: flex;
        align-items: center;
        gap: 14px;
      }
      .session-row .body {
        flex: 1 1 auto;
        min-width: 0;
      }
      .session-row .title {
        font-family: var(--font-display, inherit);
        font-weight: 700;
        font-size: 1rem;
        color: var(--s-ink, currentColor);
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .session-row .sub {
        font-size: 0.8rem;
        color: var(--s-ink-soft, #666);
        margin-top: 3px;
        display: flex;
        gap: 4px 14px;
        flex-wrap: wrap;
      }
      .balances {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        margin-top: 12px;
        font-variant-numeric: tabular-nums;
      }
      .balances .cell {
        background: var(--s-canvas-soft, #f7f7f7);
        border-radius: 10px;
        padding: 8px 10px;
      }
      .balances .label {
        font-size: 0.7rem;
        color: var(--s-ink-soft, #666);
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .balances .val {
        font-size: 0.95rem;
        font-weight: 600;
      }
      .variance-pos {
        color: var(--s-success, rgb(15, 110, 60));
      }
      .variance-neg {
        color: var(--s-danger, rgb(160, 25, 25));
      }
      .empty {
        padding: 32px 24px;
        background: var(--s-canvas-soft, #f7f7f7);
        border-radius: 16px;
        color: var(--s-ink-soft, #666);
        text-align: center;
      }
    `,
  ];

  // Tracks the shop we've loaded for so updated() can (re)load when the
  // activeShop context first arrives or changes — see updated().
  private _loadedShopName: string | null = null;

  // On a hard navigation / page refresh this component can mount before
  // the activeShop context has propagated; loading from connectedCallback
  // alone would early-return (no shop) and leave the list stuck on
  // "Loading...". Trigger the load once the shop is available, and re-run
  // it if the active shop changes. Keyed on shop name so it never loops.
  override updated(changed: Map<string | number | symbol, unknown>): void {
    super.updated(changed);
    const shop = this.activeShop?.shopName ?? null;
    if (shop && shop !== this._loadedShopName) {
      this._loadedShopName = shop;
      void this._load();
    }
  }

  private async _load(): Promise<void> {
    if (!this.activeShop) return;
    this._loading = true;
    this._error = null;
    try {
      const days = this._range === '7d' ? 7 : this._range === '30d' ? 30 : 90;
      const from = this._yyyymmdd(new Date(Date.now() - days * 86400_000));
      const api = ApiFactory.authenticated();
      const [r, shiftsR] = await Promise.all([
        api.listCashSessions(this.activeShop.shopName, { from }),
        api.listShifts(this.activeShop.shopName, { from }),
      ]);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not load sessions.';
        return;
      }
      this._sessions = r.data;
      const map: Record<string, IShift> = {};
      for (const s of shiftsR.data ?? []) {
        if (s.cashSessionId) map[s.cashSessionId] = s;
      }
      this._shiftBySessionId = map;
    } finally {
      this._loading = false;
    }
  }

  private _yyyymmdd(d: Date): string {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}${m}${day}`;
  }

  private _open(sessionId: string): void {
    router.navigate(resolveRouterPath(`cash/sessions/${sessionId}`));
  }

  private _setRange(v: '7d' | '30d' | '90d'): void {
    this._range = v;
    void this._load();
  }

  private _renderSession(s: ICashSession) {
    const shift = this._shiftBySessionId[s.id];
    const opened = shift ? new Date(shift.startedUtc) : null;
    const closed = shift?.endedUtc ? new Date(shift.endedUtc) : null;
    const deviceId = shift?.deviceId ?? '';
    const variance = s.variance ?? null;
    const varianceClass =
      variance === null
        ? ''
        : variance > 0
          ? 'variance-pos'
          : variance < 0
            ? 'variance-neg'
            : '';
    return html`
      <button class="session-card" @click=${() => this._open(s.id)}>
        <div class="session-row">
          <span class="icon-tile">
            <sl-icon name=${s.status === 'open' ? 'unlock' : 'lock'}></sl-icon>
          </span>
          <div class="body">
            <div class="title">
              ${opened ? opened.toLocaleString() : 'Session'}
              ${
                s.status === 'open'
                  ? html`<sl-badge variant="success" pill>Open</sl-badge>`
                  : html`<sl-badge variant="neutral" pill>Closed</sl-badge>`
              }
            </div>
            <div class="sub">
              ${deviceId ? html`<span>Device <code>${deviceId.slice(0, 12)}…</code></span>` : nothing}
              ${closed ? html`<span>Closed ${closed.toLocaleString()}</span>` : nothing}
            </div>
          </div>
          <sl-icon class="list-row__chevron" name="chevron-right"></sl-icon>
        </div>
        <div class="balances">
          <div class="cell">
            <div class="label">Opening float</div>
            <div class="val">${s.openingFloat.toFixed(2)}</div>
          </div>
          <div class="cell">
            <div class="label">Counted close</div>
            <div class="val">
              ${s.countedClose != null ? s.countedClose.toFixed(2) : '—'}
            </div>
          </div>
          <div class="cell">
            <div class="label">Variance</div>
            <div class="val ${varianceClass}">
              ${
                variance != null
                  ? `${variance > 0 ? '+' : ''}${variance.toFixed(2)}`
                  : '—'
              }
            </div>
          </div>
        </div>
      </button>
    `;
  }

  render() {
    if (!this.activeShop) return html`<p>No active shop.</p>`;
    return html`
      <div class="page-head">
        <h1 class="page-head__title">Cash sessions</h1>
        <p class="page-head__sub">
          Past register sessions across all devices in this shop.
        </p>
      </div>

      <div class="range-strip" role="tablist">
        <button
          class=${this._range === '7d' ? 'active' : ''}
          @click=${() => this._setRange('7d')}
        >
          7 days
        </button>
        <button
          class=${this._range === '30d' ? 'active' : ''}
          @click=${() => this._setRange('30d')}
        >
          30 days
        </button>
        <button
          class=${this._range === '90d' ? 'active' : ''}
          @click=${() => this._setRange('90d')}
        >
          90 days
        </button>
      </div>

      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._loading
          ? html`<p>Loading...</p>`
          : this._sessions.length === 0
            ? html`<div class="empty">No sessions in this range.</div>`
            : this._sessions.map((s) => this._renderSession(s))
      }
    `;
  }
}
